import RAPIER from '@dimforge/rapier3d-compat';
import * as THREE from 'three';
import type { SimWorld } from '../sim/world';
import { SURFACES } from '../sim/surfaces';

export interface SpawnPoint {
  id: string;
  name: string;
  pos: [number, number, number];
  heading: number;
}

export interface Checkpoint {
  n: number;
  name: string;
  /** Центр в мировых координатах XZ */
  pos: [number, number];
  radius: number;
}

export interface Course {
  spawns: SpawnPoint[];
  checkpoints: Checkpoint[];
  /** Примерная длина маршрута, м — для сводки после прохождения */
  lengthM: number;
}

const LANE_COLOR = 0xffd166;

/**
 * Тренировочный маршрут: один размеченный коридор, который проводит оператора
 * через все типы препятствий подряд, а не по разбросанным по полю зонам.
 * Порядок неслучаен — сначала габариты на ровном, потом покрытия, потом то,
 * что требует упреждения (лёд, уклон), и в конце точная парковка.
 *
 *   Этап 1 (вдоль +Z):  старт → слалом → узкий проход → бордюр 60 мм и тротуар
 *   Этап 2 (вдоль -X):  гравий → трава → грязь → лёд со стоп-линией
 *   Этап 3 (вдоль -Z):  уклон 10° вверх-вниз → объезд ступени 120 мм → парковка
 */
export function buildCourse(world: SimWorld): Course {
  buildGround(world);

  // ---- разметка коридора ----
  lane(world, [-1.5, -4, 1.5, 51.5], 'z');
  lane(world, [-44, 48.5, -1.5, 51.5], 'x');
  lane(world, [-43.5, 8, -40.5, 48.5], 'z');

  const checkpoints: Checkpoint[] = [];
  const cp = (n: number, name: string, pos: [number, number], radius = 2.4): void => {
    checkpoints.push({ n, name, pos, radius });
  };

  // ================= Этап 1: вдоль +Z =================

  cp(1, 'Старт', [0, 0]);
  gate(world, 1, [0, 0], 'z');

  // Слалом: конусы сбиваемые — задел габарит, увидел это в кадре
  const slalom = [
    [-0.9, 6],
    [0.9, 10],
    [-0.9, 14],
    [0.9, 18],
    [-0.9, 22],
  ] as const;
  slalom.forEach(([x, z], i) => cone(world, `конус ${i + 1}`, [x, z]));

  cp(2, 'Слалом пройден', [0, 26]);
  gate(world, 2, [0, 26], 'z');

  // Узкий проход: ширина робота 0.55 м, просвет 0.75 м
  world.addBox('стена прохода', [-0.9, 0.5, 31], [1.05, 1.0, 4], 0x6f7681);
  world.addBox('стена прохода', [0.9, 0.5, 31], [1.05, 1.0, 4], 0x6f7681);

  cp(3, 'Узкий проход', [0, 35], 2.0);
  gate(world, 3, [0, 35], 'z');

  // Тротуар: заезд через бордюр 60 мм — ровно паспортный предел ровера-S
  world.addBox('тротуар (бордюр 60 мм)', [0, 0.03, 42], [3.0, 0.06, 8], 0x8d8577);
  world.addSurfacePatch('tile', [-1.5, 38, 1.5, 46], SURFACES.tile.color, 0.066);
  sign(world, 'бордюр 60 мм', [-2.4, 38.4], Math.PI / 2);

  cp(4, 'Тротуар', [0, 42], 2.0);
  gate(world, 4, [0, 42], 'z');

  // ================= Этап 2: вдоль -X =================

  cp(5, 'Поворот на покрытия', [0, 50], 2.6);
  gate(world, 5, [0, 50], 'x');

  const patches: Array<[string, number, number]> = [
    ['gravel', -12, -4],
    ['grass', -20, -12],
    ['mud', -27, -20],
    ['snow', -38, -27],
  ];
  for (const [id, x0, x1] of patches) {
    world.addSurfacePatch(id, [x0, 48.5, x1, 51.5], SURFACES[id].color);
    sign(world, SURFACES[id].name, [(x0 + x1) / 2, 52.6], Math.PI);
  }

  cp(6, 'Гравий и трава', [-16, 50], 2.6);
  gate(world, 6, [-16, 50], 'x');

  // Стоп-линия на льду: тормозить надо начинать до неё, а не на ней
  stripe(world, [-36, 50], 3.0, 'x', 0xf05a28);
  sign(world, 'стоп-линия', [-36, 52.6], Math.PI);

  cp(7, 'Лёд: стоп-линия', [-36, 50], 2.0);

  // ================= Этап 3: вдоль -Z =================

  cp(8, 'Поворот на финишную', [-42, 48], 2.6);
  gate(world, 8, [-42, 48], 'z');

  // Уклон 10° — при паспортном пределе 12°. Подъём, площадка и съезд одним
  // телом: на стыке отдельных плит робот застревает на гребне.
  world.addRampStrip(
    'уклон 10°',
    -42,
    3,
    [
      [33, 0],
      [37, 0.7],
      [39, 0.7],
      [43, 0],
    ],
    0x9aa0a6,
  );
  sign(world, 'уклон 10°', [-44.4, 41], -Math.PI / 2);

  cp(9, 'Уклон пройден', [-42, 31], 2.6);
  gate(world, 9, [-42, 31], 'z');

  // Ступень 120 мм закрывает правую половину коридора: объезжать слева
  world.addBox('ступень 120 мм', [-41.2, 0.06, 26], [1.6, 0.12, 0.8], 0x8a4b3c);
  sign(world, 'ступень 120 мм — объезд', [-39.8, 26], -Math.PI / 2);

  cp(10, 'Объезд препятствия', [-42, 22], 2.6);
  gate(world, 10, [-42, 22], 'z');

  // Финиш: парковка в габарит, карман 0.95 м при ширине робота 0.55 м
  world.addBox('стенка кармана', [-42.55, 0.2, 11], [0.15, 0.4, 1.8], 0xc85a3a);
  world.addBox('стенка кармана', [-41.45, 0.2, 11], [0.15, 0.4, 1.8], 0xc85a3a);
  world.addBox('задняя стенка кармана', [-42, 0.2, 10.05], [1.25, 0.4, 0.15], 0xc85a3a);
  marking(world, [-42, 11], [0.95, 1.8], LANE_COLOR);
  sign(world, 'финиш — парковка', [-40.2, 11.5], -Math.PI / 2);

  cp(11, 'Финиш', [-42, 11.4], 0.9);

  // ---- точки телепорта: старт и каждый этап отдельно ----
  const spawns: SpawnPoint[] = [
    { id: 'start', name: 'Старт маршрута', pos: [0, 0.4, -2], heading: 0 },
  ];
  for (const c of checkpoints) {
    spawns.push({
      id: `cp${c.n}`,
      name: `${c.n}. ${c.name}`,
      pos: [c.pos[0], legHeight(c.pos) + 0.4, c.pos[1]],
      heading: legHeading(c.pos),
    });
  }

  return { spawns, checkpoints, lengthM: 122 };
}

/** Направление движения по маршруту в точке (эйлер по Y для стартовой позы) */
function legHeading(pos: [number, number]): number {
  if (pos[1] > 47.5 && pos[0] > -40) return -90; // этап 2 — вдоль -X
  if (pos[0] < -39) return 180; // этап 3 — вдоль -Z
  return 0; // этап 1 — вдоль +Z
}

/** Высота площадки в точке телепорта (тротуар приподнят) */
function legHeight(pos: [number, number]): number {
  if (pos[0] > -2 && pos[0] < 2 && pos[1] > 38 && pos[1] < 46) return 0.06;
  return 0;
}

function buildGround(world: SimWorld): void {
  const half = 100;
  const body = world.physics.createRigidBody(RAPIER.RigidBodyDesc.fixed());
  const col = world.physics.createCollider(
    RAPIER.ColliderDesc.cuboid(half, 0.5, half).setTranslation(0, -0.5, 0).setFriction(0.95),
    body,
  );
  world.register(col.handle, 'земля');

  const mesh = new THREE.Mesh(
    new THREE.PlaneGeometry(half * 2, half * 2),
    new THREE.MeshLambertMaterial({ color: SURFACES.asphalt.color }),
  );
  mesh.rotation.x = -Math.PI / 2;
  mesh.receiveShadow = true;
  world.scene.add(mesh);

  const grid = new THREE.GridHelper(half * 2, 100, 0x5a5f66, 0x51565c);
  grid.position.y = 0.004;
  (grid.material as THREE.Material).opacity = 0.3;
  (grid.material as THREE.Material).transparent = true;
  world.scene.add(grid);
}

/** Две линии разметки вдоль коридора: маршрут виден в кадре, а не на мини-карте */
function lane(world: SimWorld, rect: [number, number, number, number], axis: 'x' | 'z'): void {
  const [x0, z0, x1, z1] = rect;
  const w = 0.12;
  if (axis === 'z') {
    stripeMesh(world, [x0, (z0 + z1) / 2], [w, z1 - z0]);
    stripeMesh(world, [x1, (z0 + z1) / 2], [w, z1 - z0]);
  } else {
    stripeMesh(world, [(x0 + x1) / 2, z0], [x1 - x0, w]);
    stripeMesh(world, [(x0 + x1) / 2, z1], [x1 - x0, w]);
  }
}

function stripeMesh(world: SimWorld, center: [number, number], size: [number, number]): void {
  const mesh = new THREE.Mesh(
    new THREE.PlaneGeometry(size[0], size[1]),
    new THREE.MeshBasicMaterial({ color: LANE_COLOR }),
  );
  mesh.rotation.x = -Math.PI / 2;
  mesh.position.set(center[0], 0.014, center[1]);
  world.scene.add(mesh);
}

/** Поперечная полоса (стоп-линия) */
function stripe(world: SimWorld, center: [number, number], len: number, axis: 'x' | 'z', color: number): void {
  const size: [number, number] = axis === 'x' ? [0.2, len] : [len, 0.2];
  const mesh = new THREE.Mesh(
    new THREE.PlaneGeometry(size[0], size[1]),
    new THREE.MeshBasicMaterial({ color }),
  );
  mesh.rotation.x = -Math.PI / 2;
  mesh.position.set(center[0], 0.016, center[1]);
  world.scene.add(mesh);
}

/** Ворота чекпоинта: два столба и номер. Оператор ориентируется по ним, а не по карте. */
function gate(world: SimWorld, n: number, center: [number, number], axis: 'x' | 'z'): void {
  const off = 1.75;
  const dx = axis === 'z' ? off : 0;
  const dz = axis === 'z' ? 0 : off;
  for (const s of [-1, 1]) {
    const post = new THREE.Mesh(
      new THREE.BoxGeometry(0.1, 1.4, 0.1),
      new THREE.MeshLambertMaterial({ color: 0xe9edf2 }),
    );
    post.position.set(center[0] + dx * s, 0.7, center[1] + dz * s);
    post.castShadow = true;
    world.scene.add(post);
  }
  const yaw = axis === 'z' ? 0 : Math.PI / 2;
  plate(world, String(n), [center[0], center[1]], 1.6, yaw, 0.5, 0.5, 0x1b6ec2);
}

/** Табличка с текстом у края маршрута */
function sign(world: SimWorld, text: string, at: [number, number], yaw: number): void {
  plate(world, text, at, 1.0, yaw, 2.0, 0.44, 0x101317);

  const post = new THREE.Mesh(
    new THREE.BoxGeometry(0.06, 1.0, 0.06),
    new THREE.MeshLambertMaterial({ color: 0x4a4e54 }),
  );
  post.position.set(at[0], 0.5, at[1]);
  world.scene.add(post);
}

function plate(
  world: SimWorld,
  text: string,
  at: [number, number],
  y: number,
  yaw: number,
  w: number,
  h: number,
  bg: number,
): void {
  const canvas = document.createElement('canvas');
  canvas.width = 512;
  canvas.height = Math.round((512 * h) / w);
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = `#${bg.toString(16).padStart(6, '0')}`;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = '#f2f5f8';
  ctx.font = `bold ${Math.round(canvas.height * 0.55)}px system-ui, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, canvas.width / 2, canvas.height / 2);

  const mesh = new THREE.Mesh(
    new THREE.PlaneGeometry(w, h),
    new THREE.MeshBasicMaterial({ map: new THREE.CanvasTexture(canvas), side: THREE.DoubleSide }),
  );
  mesh.position.set(at[0], y, at[1]);
  mesh.rotation.y = yaw;
  world.scene.add(mesh);
}

/** Сбиваемый конус */
function cone(world: SimWorld, name: string, at: [number, number]): void {
  const mesh = new THREE.Mesh(
    new THREE.ConeGeometry(0.22, 0.5, 12),
    new THREE.MeshLambertMaterial({ color: 0xf05a28 }),
  );
  mesh.castShadow = true;
  world.addProp(name, [at[0], 0.25, at[1]], RAPIER.ColliderDesc.cylinder(0.25, 0.2), mesh, 1.4);
}

/** Контур разметки (габарит парковки) */
function marking(world: SimWorld, center: [number, number], size: [number, number], color: number): void {
  const g = new THREE.EdgesGeometry(new THREE.PlaneGeometry(size[0], size[1]));
  const line = new THREE.LineSegments(g, new THREE.LineBasicMaterial({ color }));
  line.rotation.x = -Math.PI / 2;
  line.position.set(center[0], 0.02, center[1]);
  world.scene.add(line);
}
